import { useEffect, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Wallet } from 'lucide-react'
import { api, errorMessage } from '@/lib/api'
import { cn } from '@/lib/cn'
import { money } from '@/lib/money'
import type { CustomerRow } from '@/types'
import Button from '@/components/ui/Button'
import { Field, Input } from '@/components/ui/Field'
import Modal from '@/components/ui/Modal'
import { useToast } from '@/components/ui/toast'

type Mode = 'credit' | 'debit' | 'set'

/** The server's per-adjustment ceiling, RM 1,000,000, in sen. */
const MAX_SEN = 100_000_000

const CHOICES: { value: Mode; label: string; hint: string }[] = [
  { value: 'credit', label: 'Add money', hint: 'Cash or a transfer received by hand, a goodwill credit, a correction.' },
  { value: 'debit', label: 'Deduct money', hint: 'Take money out, for example to undo a mistaken credit.' },
  { value: 'set', label: 'Set a new balance', hint: 'Type what the balance should be. The difference is recorded.' },
]

const toSen = (ringgit: number) => Math.round(ringgit * 100)

/**
 * An admin's hand correction to a customer's wallet. "Set a new balance" is
 * sent as a credit or debit of the difference, so the statement keeps one row
 * per change either way. The balance the admin was looking at travels with the
 * request, and the server refuses it if the wallet has moved since. Lifetime
 * top-up and the tier never change.
 */
export default function AdjustWalletModal({
  customer,
  balance,
  onClose,
}: {
  customer: CustomerRow | null
  /** The freshest balance the page holds; what expected_balance is checked against. */
  balance: number
  onClose: () => void
}) {
  const toast = useToast()
  const queryClient = useQueryClient()
  const [mode, setMode] = useState<Mode>('credit')
  const [amount, setAmount] = useState('')
  const [reason, setReason] = useState('')

  useEffect(() => {
    if (!customer) return
    setMode('credit')
    setAmount('')
    setReason('')
    // Reset only when the dialog opens on a customer, not on every refetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customer?.id])

  const text = amount.trim()
  const sen = /^\d+(\.\d{1,2})?$/.test(text) ? toSen(Number(text)) : null
  const balanceSen = toSen(balance)
  const deltaSen = sen === null ? 0 : mode === 'credit' ? sen : mode === 'debit' ? -sen : sen - balanceSen

  let amountError: string | undefined
  if (text !== '') {
    if (sen === null) amountError = 'Enter ringgit with up to 2 decimal places.'
    else if (mode === 'set' && deltaSen === 0) amountError = 'That is already the balance.'
    else if (mode !== 'set' && sen === 0) amountError = 'Enter an amount above RM 0.00.'
    else if (mode === 'debit' && sen > balanceSen) amountError = `Cannot deduct more than the balance of ${money(balance)}.`
    else if (Math.abs(deltaSen) > MAX_SEN) amountError = `One adjustment is at most ${money(MAX_SEN / 100)}.`
  }

  const amountOk = text !== '' && !amountError
  const ready = amountOk && reason.trim() !== ''
  const change = money(Math.abs(deltaSen) / 100)

  const refresh = (id: number) => {
    queryClient.invalidateQueries({ queryKey: ['customers-page'] })
    queryClient.invalidateQueries({ queryKey: ['customer-wallet', id] })
  }

  const save = useMutation({
    mutationFn: async (id: number) =>
      (
        await api.post<{ message?: string }>(`/admin/customers/${id}/wallet/adjustments`, {
          type: deltaSen > 0 ? 'credit' : 'debit',
          amount: Math.abs(deltaSen) / 100,
          reason: reason.trim(),
          expected_balance: balance,
        })
      ).data,
    onSuccess: (res, id) => {
      refresh(id)
      onClose()
      toast.success(res.message ?? 'Wallet adjusted')
    },
    onError: (err, id) => {
      // A moved balance is refused; refetch so the dialog shows the new figure.
      refresh(id)
      toast.error('Could not adjust the wallet', errorMessage(err))
    },
  })

  return (
    <Modal
      open={Boolean(customer)}
      onClose={onClose}
      title={customer ? `Adjust wallet - ${customer.name}` : ''}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button disabled={!ready} loading={save.isPending} onClick={() => customer && save.mutate(customer.id)}>
            <Wallet className="size-4" />
            {ready ? `${deltaSen > 0 ? 'Add' : 'Deduct'} ${change}` : 'Save'}
          </Button>
        </>
      }
    >
      {customer && (
        <div className="space-y-3">
          <p className="text-sm">
            Current balance: <span className="font-bold">{money(balance)}</span>
          </p>
          {CHOICES.map((choice) => (
            <label
              key={choice.value}
              className={cn(
                'flex cursor-pointer items-start gap-2 rounded-lg border p-2.5 text-sm',
                mode === choice.value ? 'border-primary bg-primary/5' : 'border-line',
              )}
            >
              <input
                type="radio"
                name="wallet-adjust-mode"
                checked={mode === choice.value}
                onChange={() => setMode(choice.value)}
                className="mt-0.5 size-4 accent-primary"
              />
              <span>
                <span className="font-semibold">{choice.label}</span>
                <span className="block text-xs text-muted">{choice.hint}</span>
              </span>
            </label>
          ))}
          <Field label={mode === 'set' ? 'New balance (RM)' : 'Amount (RM)'} htmlFor="wallet-amount" error={amountError}>
            <Input
              id="wallet-amount"
              type="number"
              inputMode="decimal"
              min={0}
              step="0.01"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
            />
          </Field>
          {amountOk && (
            <p className="rounded-lg border border-line bg-surface p-2.5 text-sm">
              New balance: <span className="font-bold">{money((balanceSen + deltaSen) / 100)}</span>{' '}
              <span className={cn('text-xs font-semibold', deltaSen > 0 ? 'text-status-success' : 'text-status-danger')}>
                ({deltaSen > 0 ? '+' : '-'}
                {change})
              </span>
            </p>
          )}
          <Field label="Reason" htmlFor="wallet-reason">
            <Input
              id="wallet-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              maxLength={200}
              placeholder="e.g. Cash top-up received at the counter"
            />
          </Field>
          <p className="text-xs text-muted">
            The customer sees the reason on their wallet statement. Lifetime top-up and the price tier do not change.
          </p>
        </div>
      )}
    </Modal>
  )
}
